import { useEffect, useRef } from 'react';

import { usePathname } from 'expo-router';
import { logPageView } from 'features/navigation/utils/analytics';
import { useStoreContext } from 'features/store';

import { useDistance } from 'hooks/geolocation/use-distance';
import { getStaticPathAndSearch } from 'navigation/location/location';
import { BranchEventNames } from 'state/branch/branch-event-names';
import { logBranchEvent } from 'state/branch/utils';
import { CoordinateTypes, useGeolocation } from 'state/geolocation';

// This functions receives a string like: /path?query=param and returns the query
// parameters as string
const getQueryParams = (path: string | undefined): string | undefined => {
  const query = path?.split('?')[1];
  return query;
};

// Only used for react-router and WEB
export default function useLogPageView() {
  const pathname = usePathname();
  const { store, isFetchingStore } = useStoreContext();
  const { isPermissionGranted, loadCurrentPosition, coordinatesType } = useGeolocation();
  const distanceToStoreMiles = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const lastLoggedPathname = useRef<string | null>(null);

  // uncomment to debug router state
  //useDebugLogRouterState();
  useEffect(() => {
    // Prevent a race condition where the initial PAGE_VIEW event fires instantly on app launch
    // using stale cached offline data (e.g. SICOM) before the store fetch completes.
    // We defer the event until `isFetchingStore` resolves to false, firing with fresh data.
    if (isFetchingStore) {
      return;
    }

    if (lastLoggedPathname.current !== pathname) {
      // Trigger GPS update for non-manual modes to keep coordinates fresh
      if (isPermissionGranted && coordinatesType !== CoordinateTypes.MANUAL) {
        loadCurrentPosition();
      }

      logPageView(
        pathname,
        store,
        getQueryParams(getStaticPathAndSearch()),
        distanceToStoreMiles?.toFixed(2)
      );
      logBranchEvent(BranchEventNames.PAGE_VIEW);
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.log('PATH:', getStaticPathAndSearch());
      }
      lastLoggedPathname.current = pathname;
    }
  }, [
    pathname,
    store,
    isFetchingStore,
    distanceToStoreMiles,
    coordinatesType,
    isPermissionGranted,
    loadCurrentPosition,
  ]);
}
